'use client';

/**
 * Modal Context for Interactive HTML
 *
 * Provides a way to trigger modals from anywhere in the component tree
 * without causing hydration issues. Modal triggers register themselves
 * on mount and can open modals through the context.
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface ModalConfig {
  id?: string;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  [key: string]: any; // Allow additional config
}

interface ModalContextValue {
  openModal: (config: ModalConfig) => void;
  closeModal: () => void;
  currentModal: ModalConfig | null;
  isOpen: boolean;
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within ModalProvider');
  }
  return context;
};

interface ModalProviderProps {
  children: ReactNode;
  renderModal?: (config: ModalConfig, close: () => void) => ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children, renderModal }) => {
  const [currentModal, setCurrentModal] = useState<ModalConfig | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const openModal = useCallback((config: ModalConfig) => {
    setCurrentModal(config);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => setCurrentModal(null), 300); // Allow animation to complete
  }, []);

  return (
    <ModalContext.Provider value={{ openModal, closeModal, currentModal, isOpen }}>
      {children}
      {isOpen && currentModal && renderModal && renderModal(currentModal, closeModal)}
    </ModalContext.Provider>
  );
};
